/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { Box, Heading, Text, jsx } from 'theme-ui';
import ProductGridDynamic from '../product-grid-dynamic/product-grid-dynamic';
import styles from './last-order.style';
import { getCustomerToken } from '../../helper/shop-queries';

const LastOrder: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerLoaded, setCustomerLoaded] = useState(false);

  useEffect(() => {
    async function fetchLastOrder() {
      try {
        const accessToken = await getCustomerToken();
        if (!accessToken) {
          setCustomerLoaded(true);
          setLoading(false);
          return;
        }

        setCustomerLoaded(true);
        const query = `{
          customer(customerAccessToken: "${accessToken}") {
            orders(first: 1, reverse: true) {
              edges {
                node {
                  id
                  orderNumber
                  lineItems(first: 250) {
                    edges {
                      node {
                        quantity
                        title
                        variant {
                          id
                          availableForSale
                          priceV2 {
                            amount
                            currencyCode
                          }
                          product {
                            id
                            title
                            handle
                            availableForSale
                            images(first: 1) {
                              edges {
                                node {
                                  originalSrc
                                  altText
                                }
                              }
                            }
                            variants(first: 1) {
                              edges {
                                node {
                                  id
                                  priceV2 {
                                    amount
                                    currencyCode
                                  }
                                  availableForSale
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }`;

        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/graphql',
            Accept: 'application/json',
            'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
          },
          body: query,
        };

        const response = await fetch(
          `https://${process.env.GATSBY_SHOP_NAME}.myshopify.com/api/2020-10/graphql`,
          requestOptions
        );

        const responseBody = await response.json();
        const orderEdge = responseBody?.data?.customer?.orders?.edges?.[0];

        if (!orderEdge) {
          setProducts([]);
          setLoading(false);
          return;
        }

        const mappedProducts = orderEdge.node.lineItems.edges.map(({ node }: any) => {
          const variant = node.variant || {};
          const product = variant.product || {};
          const image = product.images?.edges?.[0]?.node || null;

          return {
            node: {
              id: product.id || variant.id || node.title,
              title: product.title || node.title,
              handle: product.handle || '',
              shopifyId: product.id || variant.id || '',
              availableForSale: variant.availableForSale ?? false,
              images: {
                edges: [
                  {
                    node: image
                      ? {
                          originalSrc: image.originalSrc,
                          altText: image.altText,
                        }
                      : {
                          originalSrc: '',
                          altText: '',
                        },
                  },
                ],
              },
              variants: {
                edges: [
                  {
                    node: {
                      id: variant.id,
                      priceV2: variant.priceV2 || { amount: '0', currencyCode: '' },
                      availableForSale: variant.availableForSale ?? false,
                    },
                  },
                ],
              },
            },
          };
        });

        setProducts(mappedProducts);
      } catch (err) {
        setError('Unable to load your most recent purchase.');
      } finally {
        setLoading(false);
      }
    }

    fetchLastOrder();
  }, []);

  return (
    <Box sx={styles.wrapper}>
      <Heading as="h2" sx={styles.title}>
        Last Purchased Products
      </Heading>

      {loading && <Text sx={styles.message}>Loading your last order...</Text>}
      {!loading && !customerLoaded && (
        <Text sx={styles.message}>Sign in to view your most recent purchase.</Text>
      )}
      {!loading && customerLoaded && !error && products.length === 0 && (
        <Text sx={styles.message}>No recent order items were found.</Text>
      )}
      {error && <Text sx={styles.message}>{error}</Text>}
      {!loading && products.length > 0 && <ProductGridDynamic products={products} />}
    </Box>
  );
};

export default LastOrder;
